import { Link } from 'react-router-dom'
import { ChevronDown, Eye, Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { Button } from '@/components/ui/Button'
import type { ListingStatus, Watch } from '@/types'

/** A published piece should read as live at a glance; a draft as parked. */
const statusText: Record<ListingStatus, string> = {
  published: 'text-success',
  draft: 'text-text-muted',
  reserved: 'text-warning',
  sold: 'text-text-faint',
}

/**
 * One line of the manage-listings table.
 *
 * Not a <table>: on a phone the same markup folds into a card, which a real
 * table cannot do without horizontal scrolling. The status control is inline
 * because "hide this piece" is the thing an owner does most often.
 */
export function ListingRow({
  watch,
  onStatus,
  onDelete,
}: {
  watch: Watch
  onStatus: (status: ListingStatus) => void
  onDelete: () => void
}) {
  const { t, tf, formatPrice, formatNumber, formatDate } = useI18n()

  return (
    <div
      data-upbo-type="card"
      data-upbo-label={`Listing — ${watch.title}`}
      className={cn(
        'grid items-center gap-4 py-4 border-b border-border',
        'grid-cols-[64px_1fr_auto] lg:grid-cols-[64px_minmax(0,1fr)_140px_150px_90px_120px_auto]',
      )}
    >
      {/* Thumbnail */}
      <Link to={`/collection/${watch.id}`} className="block overflow-hidden rounded-squircle bg-surface-2">
        <img src={watch.image} alt="" loading="lazy" className="h-16 w-16 object-cover" />
      </Link>

      {/* Title + reference */}
      <div className="min-w-0 flex flex-col gap-0.5">
        <span className="font-display text-lg leading-tight truncate">{tf(watch, 'title')}</span>
        <span className="text-[11px] uppercase tracking-[0.14em] text-text-faint truncate">
          {watch.reference} · {watch.maison}
        </span>
        {/* On small screens the columns below collapse into this line. */}
        <span className="lg:hidden text-[12px] text-text-muted mt-1">
          {formatPrice(watch.price)} · {t(`status.${watch.status}`)}
        </span>
      </div>

      {/* Price */}
      <span className="hidden lg:block text-sm text-text">{formatPrice(watch.price)}</span>

      {/* Status — editable in place, in the same clothes as every other select */}
      <div className="hidden lg:block relative">
        <select
          value={watch.status}
          onChange={(e) => onStatus(e.target.value as ListingStatus)}
          aria-label={t('studio.tableStatus')}
          className={cn(
            'w-full h-9 pl-2.5 pr-8 text-[12px] appearance-none cursor-pointer rounded-squircle',
            'bg-surface border border-border text-text transition-colors',
            'focus:outline-none focus:border-text',
            statusText[watch.status],
          )}
        >
          {(['published', 'draft', 'reserved', 'sold'] as ListingStatus[]).map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-text-faint"
        />
      </div>

      {/* Views */}
      <span className="hidden lg:flex items-center gap-1.5 text-[12px] text-text-muted">
        <Eye size={13} strokeWidth={1.5} />
        {formatNumber(watch.views ?? 0)}
      </span>

      {/* Posted */}
      <span className="hidden lg:block text-[12px] text-text-muted">{formatDate(watch.created_at ?? '')}</span>

      {/* Actions */}
      <div className="flex items-center justify-end gap-1">
        <Link to={`/studio/new?id=${watch.id}`} aria-label={t('common.edit')}>
          <Button variant="ghost" size="icon">
            <Pencil size={15} strokeWidth={1.5} />
          </Button>
        </Link>
        <Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={onDelete}>
          <Trash2 size={15} strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  )
}
